/**
 * O que faz: mostra no dashboard o bloco completo "Tempo até fechar" (o mesmo da aba Vendas) para o
 *            período escolhido, com link para o detalhe em /vendas.
 * Onde usa: Dashboard, abaixo dos gráficos "Links Personalizados" e "Distribuição por Dia da Semana".
 * Conexões: useVendasPeriodoTempoFechamento, useOrigensAnuncioVendas, apelidos de anúncio do Dashboard
 *           e BlocoTempoFechamentoVendas no modo "completo".
 * Tabelas: vendas_realizadas (lead_id, data_venda), leads/metricas_conversas (datas) e origem de anúncio
 *          via RPC — tudo somente leitura com RLS e sem nome/telefone dos leads.
 * Data: 05/10/2026.
 */
import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { BlocoTempoFechamentoVendas } from '@/components/vendas/BlocoTempoFechamentoVendas';
import { useVendasPeriodoTempoFechamento } from '@/hooks/useTempoFechamentoVendas';
import { useOrigensAnuncioVendas } from '@/hooks/useOrigensAnuncioVendas';
import { parametrosPeriodoVendas } from '@/lib/vendas/painelVendas';
import { ID_BLOCO_TEMPO_FECHAMENTO, type VendaParaTempoFechamento } from '@/lib/vendas/tempoFechamento';

interface SecaoTempoFechamentoDashboardProps {
  clinicaId?: string | null;
  inicio: Date | null;
  fim: Date | null;
  periodo: string;
  // Em modo admin o link para Vendas precisa levar a clínica analisada.
  clinicaAdmin?: string;
  // Apelido do anúncio já carregado pelo Dashboard (evita outra consulta).
  apelidoDe: (nomeAnuncio: string) => string | null | undefined;
}

export function SecaoTempoFechamentoDashboard({ clinicaId, inicio, fim, periodo, clinicaAdmin, apelidoDe }: SecaoTempoFechamentoDashboardProps) {
  const vendasPeriodo = useVendasPeriodoTempoFechamento(clinicaId, inicio, fim);
  // Lista vazia estável enquanto carrega, para não disparar recálculos a cada render.
  const vendas = useMemo(() => vendasPeriodo.data ?? [], [vendasPeriodo.data]);
  const idsLeads = useMemo(() => vendas.map((venda) => venda.leadId), [vendas]);
  const origens = useOrigensAnuncioVendas(clinicaId, idsLeads);

  // Origem do lead para a comparação por campanha/conjunto/anúncio (último anúncio identificado).
  const origemDe = useCallback((venda: VendaParaTempoFechamento) => {
    const origem = origens.data?.get(venda.leadId);
    const anuncio = origem?.anuncioMeta?.trim() || null;
    return { campanha: origem?.campanha, conjunto: origem?.conjunto, anuncio, apelidoAnuncio: anuncio ? apelidoDe(anuncio) || null : null };
  }, [origens.data, apelidoDe]);

  const linkVendas = `/vendas?${parametrosPeriodoVendas(inicio, fim, periodo, clinicaAdmin)}#${ID_BLOCO_TEMPO_FECHAMENTO}`;

  return <BlocoTempoFechamentoVendas
    modo="completo"
    descricao={`Dias entre o primeiro contato do lead e a primeira venda do CRM · ${periodo}.`}
    vendas={vendas}
    vendasFiltradas={vendas}
    clinicaId={clinicaId}
    inicioPeriodo={inicio}
    origemDe={origemDe}
    carregandoOrigens={origens.isLoading}
    carregandoVendas={vendasPeriodo.isLoading}
    erroVendas={vendasPeriodo.isError}
    tentarNovamenteVendas={() => void vendasPeriodo.refetch()}
    acaoCabecalho={<Link to={linkVendas} className="inline-flex min-h-10 items-center gap-1 rounded-full px-3 text-sm font-semibold text-accent-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      Ver em Vendas<ArrowUpRight aria-hidden="true" className="h-3.5 w-3.5" />
    </Link>} />;
}
